import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Shield, Home } from 'lucide-react';
import { useLocation } from 'wouter';

const PrivacyPage: React.FC = () => {
  const [, setLocation] = useLocation();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <img 
              src="/app-logo.png" 
              alt="Tectonix" 
              className="h-12 w-12 mr-3 object-contain"
              style={{ filter: 'brightness(0)' }}
            />
            <h1 className="text-3xl font-bold text-gray-900">Tectonix</h1>
          </div>
          <div className="flex items-center justify-center gap-2 mb-2">
            <Shield className="h-6 w-6 text-blue-600" />
            <h2 className="text-2xl font-semibold text-gray-800">Privacy Policy</h2>
          </div>
          <p className="text-sm text-gray-500">Last updated: {new Date().toLocaleDateString()}</p>
        </div>

        {/* Content */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Privacy Policy</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6 prose max-w-none">
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">1. Introduction</h3>
              <p className="text-gray-700 leading-relaxed">
                Welcome to Tectonix Procurement Management Platform ("we," "our," or "us"). 
                We are committed to protecting your privacy and ensuring the security of your personal information. 
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use 
                our platform integrated with Autodesk Construction Cloud (ACC).
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">2. Information We Collect</h3>
              <div className="space-y-3">
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">2.1 Authentication Information</h4>
                  <p className="text-gray-700 leading-relaxed">
                    When you sign in using Autodesk Construction Cloud OAuth, we collect:
                  </p>
                  <ul className="list-disc list-inside text-gray-700 ml-4 space-y-1">
                    <li>Your Autodesk account information (name, email, profile image)</li>
                    <li>OAuth tokens for accessing Autodesk Construction Cloud services</li>
                    <li>Session information to maintain your login state</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">2.2 Project and Usage Data</h4>
                  <p className="text-gray-700 leading-relaxed">
                    We collect information about:
                  </p>
                  <ul className="list-disc list-inside text-gray-700 ml-4 space-y-1">
                    <li>Projects you access through the platform</li>
                    <li>Your interactions with tender packages, documents, and other features</li>
                    <li>Access logs and activity timestamps</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">2.3 Tenderer Information</h4>
                  <p className="text-gray-700 leading-relaxed">
                    For tenderer users accessing via email and access code:
                  </p>
                  <ul className="list-disc list-inside text-gray-700 ml-4 space-y-1">
                    <li>Email address used for authentication</li>
                    <li>Company information associated with your access code</li>
                    <li>Access history and download records</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">3. How We Use Your Information</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                We use the collected information for the following purposes:
              </p>
              <ul className="list-disc list-inside text-gray-700 ml-4 space-y-2">
                <li><strong>Authentication and Access:</strong> To verify your identity and provide secure access to the platform</li>
                <li><strong>Service Delivery:</strong> To enable procurement management features and integrate with Autodesk Construction Cloud</li>
                <li><strong>Project Management:</strong> To display and manage projects, tender packages, and related documents</li>
                <li><strong>Access Control:</strong> To enforce role-based permissions and ensure appropriate data access</li>
                <li><strong>Analytics:</strong> To improve our services and understand platform usage patterns</li>
                <li><strong>Communication:</strong> To send important updates and notifications related to your account</li>
              </ul>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">4. Data Sharing and Disclosure</h3>
              <div className="space-y-3">
                <p className="text-gray-700 leading-relaxed">
                  We do not sell your personal information. We may share your information only in the following circumstances:
                </p>
                <ul className="list-disc list-inside text-gray-700 ml-4 space-y-2">
                  <li><strong>Autodesk Construction Cloud:</strong> We integrate with ACC services and may share necessary information to provide platform functionality</li>
                  <li><strong>Service Providers:</strong> We may share data with trusted third-party service providers who assist in platform operations</li>
                  <li><strong>Legal Requirements:</strong> We may disclose information if required by law or to protect our rights</li>
                  <li><strong>Business Transfers:</strong> In the event of a merger or acquisition, your information may be transferred</li>
                </ul>
              </div>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">5. Data Security</h3>
              <p className="text-gray-700 leading-relaxed">
                We implement industry-standard security measures to protect your information:
              </p>
              <ul className="list-disc list-inside text-gray-700 ml-4 space-y-2 mt-3">
                <li>Encrypted data transmission using HTTPS/TLS</li>
                <li>Secure OAuth token storage and management</li>
                <li>Role-based access control and permission systems</li>
                <li>Regular security audits and updates</li>
                <li>Session management and timeout controls</li>
              </ul>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">6. Data Retention</h3>
              <p className="text-gray-700 leading-relaxed">
                We retain your personal information for as long as necessary to provide our services and comply with legal obligations. 
                When you request account deletion, we will remove your personal data in accordance with applicable data protection laws.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">7. Your Rights</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                Depending on your jurisdiction, you may have the following rights:
              </p>
              <ul className="list-disc list-inside text-gray-700 ml-4 space-y-2">
                <li>Access your personal information</li>
                <li>Correct inaccurate or incomplete data</li>
                <li>Request deletion of your personal information</li>
                <li>Object to processing of your information</li>
                <li>Data portability</li>
                <li>Withdraw consent where processing is based on consent</li>
              </ul>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">8. Cookies and Tracking</h3>
              <p className="text-gray-700 leading-relaxed">
                We use session cookies and local storage to maintain your login state and preferences. 
                These are essential for platform functionality and are not used for tracking purposes beyond session management.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">9. Third-Party Services</h3>
              <p className="text-gray-700 leading-relaxed">
                Our platform integrates with Autodesk Construction Cloud. Your use of ACC services is also governed by 
                Autodesk's privacy policy. We encourage you to review Autodesk's privacy practices.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">10. Children's Privacy</h3>
              <p className="text-gray-700 leading-relaxed">
                Our platform is not intended for users under the age of 18. We do not knowingly collect personal information from children.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">11. Changes to This Policy</h3>
              <p className="text-gray-700 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any material changes by posting 
                the new policy on this page and updating the "Last updated" date. Your continued use of the platform after 
                such changes constitutes acceptance of the updated policy.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">12. Contact Us</h3>
              <p className="text-gray-700 leading-relaxed">
                If you have questions or concerns about this Privacy Policy or our data practices, please contact us at:
              </p>
              <div className="mt-3 p-4 bg-gray-50 rounded-lg">
                <p className="text-gray-800 font-semibold">Tectonix Procurement Platform</p>
                <p className="text-gray-700">Email: <a href="mailto:admin@moatcity.com" className="text-blue-600 hover:text-blue-800 underline">admin@moatcity.com</a></p>
              </div>
            </section>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-8 text-center">
          <button
            onClick={() => setLocation('/')}
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
          >
            <Home className="h-4 w-4" />
            Return to Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;

